import { ChangeDetectorRef, DestroyRef, EventEmitter, inject, SimpleChanges } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, UntypedFormArray, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { takeWhileAlive } from '@core/frontend-shared/utils';
import { Model } from '@core/shared/model';
import { BehaviorSubject, bufferCount, combineLatest, filter, map, share, take, takeUntil } from 'rxjs';
import { FsOnAfterInitEvent, ModelFormServiceFactory, whenFormServiceInitialized$ } from '../form-service';
import * as i0 from "@angular/core";
import * as i1 from "../form-service";
/**
 * ModelBasedControlValueAccessor is a base class to handle form values.
 * In most use cases, you should use simple-fieldset or array-fieldset which extend this class.
 *
 */
export let ModelBasedControlValueAccessor = /*#__PURE__*/(() => {
  class ModelBasedControlValueAccessor {
    get initialized() {
      return this.initialized$.value;
    }
    get formServiceInitialized() {
      return this.formServiceInitialized$.value;
    }
    set _debug(val) {
      this.debug = val;
      if (this.formService) this.formService.debug();
    }
    set _formService(service) {
      if (this.initialized) {
        this.reset();
        setTimeout(() => {
          this.formService$.next(service);
          this._tryToInitialize();
        }, 5);
      } else {
        this.formService$.next(service);
      }
    }
    get formService() {
      return this.formService$.getValue();
    }
    set _Model(val) {
      if (!val || typeof val !== 'function') throw new Error('variable provided as Model is no constructor! ' + val);
      if (!(val.prototype instanceof Model)) throw new Error('value provided as Model does not extend Model! ' + val);
      this.Model = val;
    }
    set _value(value) {
      // console.warn('modelBasedCVA value input is DEPRECATED')
      this.value = value;
    }
    set _formGroup(val) {
      if (!(val instanceof UntypedFormGroup)) throw new Error('value provided as FormGroup is no FormGroup! ' + val);
      this.formGroup = val;
    }
    set controlFormServiceChildProp(info) {
      this.parentFormService = info[0];
      this.useParentFormServiceFormControl = info[1];
    }
    constructor(cd, formServiceFactory) {
      this.cd = cd;
      this.formServiceFactory = formServiceFactory;
      this.destroyRef = inject(DestroyRef);
      this.initialized$ = new BehaviorSubject(false);
      this.formServiceInitialized$ = new BehaviorSubject(false);
      this.allInitialized$ = combineLatest([this.initialized$, this.formServiceInitialized$]).pipe(map(([selfReady, serviceReady]) => {
        return selfReady && serviceReady;
      }));
      this.isDev = !IS_PRODUCTION;
      this.focused = false;
      this.disabled = false;
      this.readonly = false;
      this.repeatable = false;
      this.debug = false;
      this.focus = new EventEmitter();
      this.blur = new EventEmitter();
      this.valueChanged = new EventEmitter(); // does not fire when SETTING value via CVA, only when it is being changed!
      // @Output() onChange: EventEmitter<any> = new EventEmitter();
      this.cvaNotifyChanged = val => {}; // Called on a value change
      this.cvaNotifyTouched = () => {}; // Called if you care if the form was touched
      this.notifyValidatorChange = () => {}; // Called on a validator change or re-validation;
      this.formService$ = new BehaviorSubject(null);
      this.initializedFormService$ = whenFormServiceInitialized$(this.formService$);
      this.initializedFormService = toSignal(this.initializedFormService$);
      // note that it will trigger twice because we always update formService$ to null, then to new formService
      this.formServiceChange$ = this.formService$.pipe(bufferCount(2, 1), map(([a, b]) => {
        return !a || !b || a._id !== b._id;
      }), filter(changed => changed), share(), takeWhileAlive(this.destroyRef));
      // allows to receive auto-generated formService. Fires once right at end of initialization.
      this.formServiceOutput = new EventEmitter();
    }
    ngOnChanges(changes) {
      // every time any Input is bound, the class will check if enough information is available to initialize.
      // if so, it executes initalization.
      // this seems to cause issues because it may initialize setup before it is supposed to be.
      // formService.initialize should be called manually, may be auto-executed by this
      if (!this.initialized) this._tryToInitialize();
    }
    _tryToInitialize() {
      if (this.initialized) return;
      const canSetupUsingFormService = !!this.formService;
      const canSetupUsingParentFormServiceFormControl = this.parentFormService && typeof this.useParentFormServiceFormControl !== 'undefined';
      const canSetupUsingModel = this.Model && !!this.formGroup;
      if (this.debug) console.log('[DEBUG] ModelBasedCVA: initialize possible?', canSetupUsingFormService, canSetupUsingParentFormServiceFormControl, canSetupUsingModel, this);
      if (canSetupUsingFormService) {
        if (this.debug) console.log('[DEBUG] ModelBasedCVA: initialize canSetupUsingFormService');
        // wait for formService to notify of initialization
        this.formService.initialized$.pipe(filter(value => !!value), take(1)).subscribe(status => {
          if (!status) return;
          this.value = this.formService.getValue();
          this._executeInitialization();
        });
        if (!IS_PRODUCTION) {
          setTimeout(() => {
            if (!this.formService.initialized) {
              console.warn('ModelBasedCVA detected an issue: assigned formService has not been initialized. Passed FormServices are not auto-initialized anymore! Make sure to intialize them manually.', this.formService);
            }
          }, 2000);
        }
      } else if (canSetupUsingParentFormServiceFormControl) {
        if (this.debug) console.log('[DEBUG] ModelBasedCVA: initialize canSetupUsingParentFormServiceFormControl');
        const service = this.parentFormService;
        const expectArray = service.dataIsArray();
        let formGroup;
        if (expectArray) {
          // parent is formArray, controlName is array index, all use same model
          const controlName = this.useParentFormServiceFormControl;
          this.Model = service.modelFactory.Model;
          formGroup = this.parentFormService.formGroup.controls[controlName];
        } else {
          // parent is formGroup, controlName is formGroup key, load nested model
          const controlName = this.useParentFormServiceFormControl;
          const propInfo = this.parentFormService.getModelPropTypeInfo(controlName);
          if (!propInfo) throw Error('Initialize using ParentFormServiceFormControl failed: Prop ' + controlName + ' has no type info');
          this.Model = propInfo.Model;
          formGroup = this.parentFormService.formGroup.get(controlName);
        }
        if (!(formGroup instanceof UntypedFormGroup)) throw Error('control to be used for subform is no FormGroup! ' + this.useParentFormServiceFormControl + ': ' + formGroup);
        this.createDefaultFormService();
        this.formService.setModel(this.Model);
        this.formService.setFormGroup(formGroup);
        this._executeInitialization({
          initializeFormService: true
        });
      } else if (canSetupUsingModel) {
        if (this.debug) console.log('[DEBUG] ModelBasedCVA: initialize canSetupUsingModel');
        this.createDefaultFormService();
        this.formService.setFormGroup(this.formGroup);
        this.formService.setModel(this.Model);
        this._executeInitialization({
          initializeFormService: true
        });
      }
      this.cd.markForCheck();
    }
    _executeInitialization(options = {}) {
      if (this.debug) this.formService.debug();
      // if a custom formService is used, it must be initialzed now
      if (options.initializeFormService) this.formService.initialize();
      this.bindFormServiceObservables();
      this.initialized$.next(true);
    }
    createDefaultFormService() {
      this.formService$.next(this.formServiceFactory.useDestroyRef(this.destroyRef).createUninitialized());
      if (this.debug) this.formService.debug();
    }
    bindFormServiceObservables() {
      // NOTE: formService may change! Ensure all bindings are unsubscribing when formServiceChange$ emits!
      this.formService.initializedValue$.pipe(takeWhileAlive(this.destroyRef), filter(status => !!status), take(1)).subscribe(initialized => {
        this.formServiceInitialized$.next(true);
      });
      this.formService.formValueChanges$.pipe(takeWhileAlive(this.destroyRef), takeUntil(this.formServiceChange$)).subscribe(data => {
        this.valueChanged.emit(data);
        this.cvaNotifyChanged(data);
        this.notifyValidatorChange();
        // this.cd.detectChanges()
      });
      // fixes occasional ExpressionChangedAfterItHasBeenCheckedErrors
      // (which never seemed to inflict UI issues, but with this fix angular is happy.)
      this.formService.state$.pipe(takeWhileAlive(this.destroyRef), takeUntil(this.formServiceChange$)).subscribe(state => {
        this.cd.detectChanges();
      });
      // must not emit formService before it is ready - wait for FsOnAfterInitEvent.
      this.formService.events$.pipe(filter(e => e instanceof FsOnAfterInitEvent), take(1)).subscribe(init => {
        this.formServiceOutput.emit(this.formService);
      });
    }
    reset() {
      this.initialized$.next(false);
      this.formServiceInitialized$.next(false);
      this.formService$.next(null);
      this.cd.markForCheck();
    }
    writeValue(value) {
      this._value = value;
      this.cd.markForCheck();
      this.notifyValidatorChange();
    }
    registerOnChange(fn) {
      this.cvaNotifyChanged = fn;
    }
    registerOnTouched(fn) {
      this.cvaNotifyTouched = fn;
    }
    setDisabledState(val) {
      this.disabled = val;
    }
    /**
     * Method that performs synchronous validation against the provided control.
     * Not required for Model driven forms.
     * Called by angular. Validate is part of the Validator interface
     */
    validate(control) {
      const status = this.formService?.formGroup?.status;
      if (!status) return {
        invalid: true
      };
      return status === 'VALID' ? null : {
        invalid: true
      };
    }
    registerOnValidatorChange(fn) {
      this.notifyValidatorChange = fn;
    }
    focusInput(event) {
      this.focused = true;
      this.focus.emit();
      this.cd.markForCheck();
    }
    blurInput(event) {
      this.focused = false;
      this.blur.emit();
      this.cvaNotifyTouched();
      this.cd.markForCheck();
    }
    static {
      this.ɵfac = function ModelBasedControlValueAccessor_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ModelBasedControlValueAccessor)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1.ModelFormServiceFactory));
      };
    }
    static {
      this.ɵdir = /*@__PURE__*/i0.ɵɵdefineDirective({
        type: ModelBasedControlValueAccessor,
        inputs: {
          disabled: "disabled",
          readonly: "readonly",
          repeatable: "repeatable",
          _debug: [0, "debug", "_debug"],
          _formService: [0, "formService", "_formService"],
          _Model: [0, "Model", "_Model"],
          _value: [0, "value", "_value"],
          _formGroup: [0, "formGroup", "_formGroup"],
          controlFormServiceChildProp: "controlFormServiceChildProp"
        },
        outputs: {
          focus: "focus",
          blur: "blur",
          valueChanged: "valueChanged",
          formServiceOutput: "formService"
        },
        features: [i0.ɵɵNgOnChangesFeature]
      });
    }
  }
  return ModelBasedControlValueAccessor;
})();